import { Suspense, useEffect, useRef } from "react";
import { Canvas, useThree, useFrame } from "@react-three/fiber";
import { useTexture } from "@react-three/drei";
import * as THREE from "three";

function Dome({ url, equirect }) {
  const tex = useTexture(url);
  useEffect(() => { if (tex) tex.colorSpace = THREE.SRGBColorSpace; }, [tex]);
  if (equirect) {
    // True 360° equirectangular panorama mapped to the inside of a sphere.
    return (
      <mesh scale={[-1, 1, 1]}>
        <sphereGeometry args={[12, 64, 40]} />
        <meshBasicMaterial map={tex} side={THREE.BackSide} toneMapped={false} />
      </mesh>
    );
  }
  // Flat render: wrap on the inside of an open cylinder (no polar distortion).
  return (
    <mesh>
      <cylinderGeometry args={[10, 10, 19, 64, 1, true]} />
      <meshBasicMaterial map={tex} side={THREE.BackSide} toneMapped={false} />
    </mesh>
  );
}

function LookControls() {
  const { camera, gl } = useThree();
  const s = useRef({ down: false, lon: 90, lat: 0, px: 0, py: 0 });
  useEffect(() => {
    const el = gl.domElement;
    el.style.cursor = "grab";
    const down = (e) => { s.current.down = true; s.current.px = e.clientX; s.current.py = e.clientY; el.style.cursor = "grabbing"; };
    const up = () => { s.current.down = false; el.style.cursor = "grab"; };
    const move = (e) => {
      if (!s.current.down) return;
      s.current.lon -= (e.clientX - s.current.px) * 0.18;
      s.current.lat += (e.clientY - s.current.py) * 0.18;
      s.current.lat = Math.max(-32, Math.min(32, s.current.lat));
      s.current.px = e.clientX; s.current.py = e.clientY;
    };
    const wheel = (e) => {
      e.preventDefault();
      camera.fov = Math.max(35, Math.min(95, camera.fov + e.deltaY * 0.04));
      camera.updateProjectionMatrix();
    };
    el.addEventListener("pointerdown", down);
    window.addEventListener("pointerup", up);
    el.addEventListener("pointermove", move);
    el.addEventListener("wheel", wheel, { passive: false });
    return () => {
      el.removeEventListener("pointerdown", down);
      window.removeEventListener("pointerup", up);
      el.removeEventListener("pointermove", move);
      el.removeEventListener("wheel", wheel);
    };
  }, [camera, gl]);
  useFrame(() => {
    const phi = THREE.MathUtils.degToRad(90 - s.current.lat);
    const theta = THREE.MathUtils.degToRad(s.current.lon);
    const t = new THREE.Vector3(
      Math.sin(phi) * Math.cos(theta),
      Math.cos(phi),
      Math.sin(phi) * Math.sin(theta),
    );
    camera.lookAt(t);
  });
  return null;
}

export default function PanoramaViewer({ imageUrl, equirect = false }) {
  return (
    <Canvas camera={{ position: [0, 0, 0.1], fov: 70 }} gl={{ antialias: true }} data-testid="panorama-canvas">
      <Suspense fallback={null}>
        <Dome url={imageUrl} equirect={equirect} />
      </Suspense>
      <LookControls />
    </Canvas>
  );
}
